import { type CollisionDetection, closestCenter, rectIntersection } from '@dnd-kit/core';

/**
 * Custom collision detection for layouts with both:
 * - Individual droppable items (e.g., "replace this card" targets)
 * - A wider droppable area near or around them (e.g., "insert here" zones)
 *
 * Behavior: if the drag is mostly over a card (>= 50% of the drag's area overlaps
 * one of the items), that card wins — the user clearly intends to replace. Otherwise
 * the drop area wins, so dragging into the gap between cards triggers insert.
 *
 * This matches the user's mental model: "if I'm hovering on a card, replace; if I'm
 * hovering between cards, insert." It does NOT use vertical pointer position to
 * arbitrate, because insert zones are often taller than cards (e.g., a tall T-bar
 * gutter between shorter cards), which would otherwise penalize the upper portion
 * of every card as "insert" even when the drag is fully over it.
 *
 * Features:
 * - Uses rectIntersection for forgiving collision detection (any overlap, not just pointer position)
 * - Requires >= 50% area overlap with a card before treating it as a replace target
 * - Falls back to drop area when no card qualifies AND the pointer is near the drop area
 *   (with a 20px vertical margin for forgiveness)
 * - Falls back to closestCenter for keyboard navigation
 *
 * @param dropAreaPrefix - The ID prefix for the droppable area (e.g., 'carousel-drop-area')
 * @param itemPrefix - The ID prefix for individual droppable items (e.g., 'carousel-')
 *
 * @example
 * ```tsx
 * const collisionDetection = createPrioritizedDropAreaCollision('carousel-drop-area', 'carousel-');
 *
 * <DndContext collisionDetection={collisionDetection}>
 *   // Your drag and drop components
 * </DndContext>
 * ```
 */
export function createPrioritizedDropAreaCollision(dropAreaPrefix: string, itemPrefix: string): CollisionDetection {
  return (args) => {
    const { droppableRects, pointerCoordinates, collisionRect } = args;

    // Without a collisionRect there's nothing geometric to compare against; bail early.
    // dnd-kit's rectIntersection crashes when iterating containers with a null active rect.
    if (!collisionRect) return [];

    // Use rectIntersection first to get all nearby collisions (more forgiving than pointerWithin)
    const nearbyCollisions = rectIntersection(args);

    const dropAreaCollision = nearbyCollisions.find((collision) => String(collision.id).startsWith(dropAreaPrefix));

    // Filter to items with >= 50% overlap. 50% means "the drag is more over this card
    // than not" — a single drag can only satisfy this for one card at a time, so
    // neighbouring cards can never both qualify.
    const itemCollisions = nearbyCollisions
      .filter(
        (collision) => String(collision.id).startsWith(itemPrefix) && !String(collision.id).startsWith(dropAreaPrefix),
      )
      .filter((collision) => {
        if (!collisionRect) return false;

        const itemRect = droppableRects.get(collision.id);
        if (!itemRect) return false;

        const intersectionLeft = Math.max(collisionRect.left, itemRect.left);
        const intersectionTop = Math.max(collisionRect.top, itemRect.top);
        const intersectionRight = Math.min(collisionRect.right, itemRect.right);
        const intersectionBottom = Math.min(collisionRect.bottom, itemRect.bottom);

        if (intersectionRight <= intersectionLeft || intersectionBottom <= intersectionTop) {
          return false;
        }

        const intersectionArea = (intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop);
        const collisionRectArea = collisionRect.width * collisionRect.height;

        const overlapPercentage = intersectionArea / collisionRectArea;
        return overlapPercentage >= 0.5;
      });

    // Items always win when they qualify — replacing a card is a clear, intentional action.
    if (itemCollisions.length > 0) {
      return itemCollisions;
    }

    // No card qualifies for replace. Fall back to the drop area if the pointer is near it
    // (with 20px vertical forgiveness so the user doesn't have to be pixel-perfect).
    if (dropAreaCollision && pointerCoordinates) {
      const dropAreaRect = droppableRects.get(dropAreaCollision.id);
      if (dropAreaRect) {
        const verticalMargin = 20;
        const isNearDropAreaVertically =
          pointerCoordinates.y >= dropAreaRect.top - verticalMargin &&
          pointerCoordinates.y <= dropAreaRect.bottom + verticalMargin;

        if (isNearDropAreaVertically) {
          return [dropAreaCollision];
        }
      }
    }

    if (dropAreaCollision) {
      return [dropAreaCollision];
    }

    // For keyboard navigation or when no rect intersections found,
    // use closestCenter to find the nearest droppable (only if collisionRect exists)
    if (collisionRect) {
      const closestCenterCollisions = closestCenter(args);
      if (closestCenterCollisions.length > 0) {
        return closestCenterCollisions;
      }
    }

    return [];
  };
}
